import React, { useEffect, useState } from 'react'
import { ReactComponent as UpArrow } from "../../assets/icons/upArrow.svg";
import { ReactComponent as DownArrow } from "../../assets/icons/downArrow.svg";
import AreaChart from "./charts/AreaChart";
import HideData from "./HideData.jsx";
import HorizontalBarChart from "./charts/HorizontalBarChart";
import VerticalBarChart from "./charts/VerticalBarChart";
import LineChart from "./charts/LineChart";
import { ReactComponent as Spinner } from "../../assets/icons/spinner.svg";
import { useGetCompanyFinanceChartDataMutation } from '../../store/Company/companyApiSlice.js';
import { useSelector } from 'react-redux';
import { currencyDetails } from '../../utils/currency.js';
import { convertLetterCase } from '../../utils/Helper.js';

function FinancialCharts({ edit = false, setShowWarning, companyId, setGraphPresent, setShowUploadInput, resData }) {
    const [graphData, setGraphData] = useState([]);
    const { currentEditingCompany } = useSelector((state) => state.company);
    const { token } = useSelector((state) => state.auth);
    const [revenueProfitChart, setRevenueProfitChart] = useState([]);
    const [roeRoaChart, setRoeRoaChart] = useState([]);
    const [quickAssetChart, setQuickAssetChart] = useState([]);
    const [freeCashChart, setFreeChart] = useState([]);
    const [operatingCashFlowChart, setOperatingCashFlowChart] = useState([]);
    const [cashConversionCycleChart, setCashConversionCycleChart] = useState([])
    const [efficiencyRatioChart, setEfficiencyRatioChart] = useState([])
    const [keyFinancialRatioChart, setKeyFinancialRatioChart] = useState([])
    const [getCompanyFinanceChartData] = useGetCompanyFinanceChartDataMutation();
    const [showGraph, setShowGraph] = useState(false)
    const [isLoading, setIsLoading] = useState(false);
    const getGraphData = async () => {
        setIsLoading(true)
        const company_id = companyId ? companyId : currentEditingCompany?.companyID
        const startsWithTRX = company_id.startsWith("TRX");
        if (!edit && token && startsWithTRX) {
            console.log(startsWithTRX, "startsWithTRX")
            resData = await getCompanyFinanceChartData({ token: token, company_id: company_id, location: 'company-profile' }).unwrap();
        }
        const revenueProfitData = resData?.profitability?.revenue_to_earnings_graph?.data ?? [];
        const roeRoaChartData = resData?.profitability?.roe_to_roa?.data ?? [];
        const quickAssetsToLiabilities = resData?.liquidity?.quick_assets_to_liabilities?.data ?? [];
        const operatingCashFlowRatio = resData?.liquidity?.operating_cash_flow_ratio?.data ?? [];
        const freeCashFlowRatio = resData?.liquidity?.free_cash_flow_ratio?.data ?? [];
        const cashConversionCycleRatio = resData?.efficiency?.cash_conversion_cycle?.data ?? [];
        const efficiencyRatio = resData?.efficiency?.efficiency_ratios?.data ?? [];
        const keyFinancials = resData?.financial ?? [];
        // if (edit) {
        //     (revenueProfitData?.length > 0 || roeRoaChartData?.length > 0 || quickAssetsToLiabilities?.length > 0 || operatingCashFlowRatio?.length > 0 || freeCashFlowRatio?.length > 0 || cashConversionCycleRatio?.length > 0 || efficiencyRatio?.length > 0 || keyFinancials?.length > 0) ? setIsGraphDataAvailable(resData) : setIsGraphDataAvailable(false);
        // }
        if (
            revenueProfitData?.length > 0 || roeRoaChartData?.length > 0 || quickAssetsToLiabilities?.length > 0 || operatingCashFlowRatio?.length > 0 || freeCashFlowRatio?.length > 0 || cashConversionCycleRatio?.length > 0 || efficiencyRatio?.length > 0 || keyFinancials?.length > 0) {
            setShowGraph(true);
            setShowUploadInput(false);
        } else {
            setShowGraph(false);
            setShowUploadInput(true);
        }

        //Profitability
        const revenueLabels = revenueProfitData?.map(entry => entry?.year) ?? [];
        const netProfitData = revenueProfitData?.map(entry => entry?.net_profit) ?? [];
        const revenueData = revenueProfitData?.map(entry => entry?.revenue) ?? [];

        const roeRoaLabels = roeRoaChartData?.map(entry => entry?.year) ?? [];
        const roeData = roeRoaChartData?.map(entry => entry?.roe) ?? [];
        const roaData = roeRoaChartData?.map(entry => entry?.roa) ?? [];
        //Liquidity
        const quickAssetsToLiabilitiesLabels = quickAssetsToLiabilities?.map(entry => entry?.year) ?? [];
        const quickAssetsData = quickAssetsToLiabilities?.map(entry => entry?.quick_assets) ?? [];
        const totalCurrentLiabilitiesData = quickAssetsToLiabilities?.map(entry => entry?.total_current_liabilities) ?? [];

        const operatingCashFlowLabels = operatingCashFlowRatio?.map(entry => entry?.year) ?? [];
        const cashFlowOperationsData = operatingCashFlowRatio?.map(entry => entry?.cash_flow_operations) ?? [];
        const cashflowCoverageRatio = operatingCashFlowRatio?.map(entry => entry?.cashflow_coverage_ratio) ?? [];

        const freeCashFlowRatioLabels = freeCashFlowRatio?.map(entry => entry?.year) ?? [];
        const freeCashFlowData = freeCashFlowRatio?.map(entry => entry?.free_cash_flow) ?? [];
        //Efficiency
        const cashConversionCycleRatioLabels = cashConversionCycleRatio?.map(entry => entry?.year) ?? [];
        const cashConversionCycleData = cashConversionCycleRatio?.map(entry => entry?.cash_conversion_cycle) ?? [];

        const efficiencyRatioLabels = efficiencyRatio?.map(entry => entry?.year) ?? [];
        const daySalesOutstanding = efficiencyRatio?.map(entry => entry?.day_sales_outstanding) ?? [];
        const dayPayablesOutstanding = efficiencyRatio?.map(entry => entry?.day_payables_outstanding) ?? [];
        const daysInventoryOutstanding = efficiencyRatio?.map(entry => entry?.days_inventory_outstanding) ?? [];

        const unitCurrencyText = resData?.units + " " + "(" + currencyDetails?.find(currency => currency?.name === resData?.currency)?.cc + ")";

        setRevenueProfitChart([{
            labels: revenueLabels,
            datasets: [
                {
                    fill: true,
                    label: 'Net Profit',
                    data: netProfitData,
                    borderColor: 'rgb(250,172,144)',
                    backgroundColor: 'rgba(250,172,144, 1)',
                },
                {
                    fill: true,
                    label: 'Revenue',
                    data: revenueData,
                    borderColor: 'rgb(155,173,219)',
                    backgroundColor: 'rgba(155,173,219, 1)',
                },
            ],
        },
        {
            responsive: true,
            scales: {
                y: {
                    title: {
                        display: true,
                        text: unitCurrencyText,
                        font: {
                            size: '10px',
                        }
                    },
                    ticks: {
                        callback: function (value) {
                            return value.toFixed(2);
                        },
                    },
                }
            },
            plugins: {
                legend: {
                    position: 'top',
                    labels: {
                        boxWidth: 10,
                        boxHeight: 10,
                    },
                },
                // title: {
                //     display: true,
                //     text: 'Revenue to Profit',
                // },
            },
        }]);

        setRoeRoaChart([{
            labels: roeRoaLabels,
            datasets: [
                {
                    label: 'ROA',
                    data: roaData?.map((value) => value.toFixed(2)),
                    backgroundColor: 'rgb(240,139,73)',
                    stack: 'Stack 0',
                },
                {
                    label: 'ROE',
                    data: roeData?.map((value) => value.toFixed(2)),
                    backgroundColor: 'rgb(72,119,199)',
                    stack: 'Stack 1',
                }
            ],
        },
        {
            responsive: true,
            plugins: {
                legend: {
                    position: 'top',
                    labels: {
                        boxWidth: 10,
                        boxHeight: 10,
                    },
                },
                // title: {
                //     display: true,
                //     text: 'PROFITABILITY',
                // },
            },
            scales: {
                y: {
                    stacked: true,
                    beginAtZero: true,
                    ticks: {
                        callback: function (value) {
                            return (value * 100).toFixed(2);
                        },
                    },
                    // title: {
                    //     display: true,
                    //     text: "times",
                    //     font: {
                    //         size: '10px',
                    //     }
                    // }
                },
                // y: {
                //     stacked: true,
                // },
            },
        }])

        setQuickAssetChart([{
            labels: quickAssetsToLiabilitiesLabels,
            datasets: [
                {
                    label: 'Quick Assets',
                    data: quickAssetsData?.map((value) => value.toFixed(2)),
                    backgroundColor: 'rgb(68,114,196)',
                    barPercentage: 1, // Adjust the width of the bars for the dataset
                },
                {
                    label: 'Current Liabilities',
                    data: totalCurrentLiabilitiesData?.map((value) => value.toFixed(2)),
                    backgroundColor: 'rgb(237,125,49)',
                    barPercentage: 1, // Adjust the width of the bars for the dataset
                },
            ],
        },
        {
            responsive: true,
            scales: {
                y: {
                    title: {
                        display: true,
                        text: unitCurrencyText,
                        font: {
                            size: '10px',
                        }
                    },
                    ticks: {
                        callback: function (value) {
                            return value.toFixed(2);
                        },
                    },
                }
            },
            plugins: {
                legend: {
                    position: 'top',
                    labels: {
                        boxWidth: 10,
                        boxHeight: 10,
                    },
                },
                // title: {
                //     display: true,
                //     text: 'Liquidity',
                // },
            },
        }])

        setFreeChart([{
            labels: freeCashFlowRatioLabels,
            datasets: [
                {
                    label: 'Cash Flow',
                    data: freeCashFlowData?.map((value) => value.toFixed(2)),
                    borderColor: 'rgb(155,173,219)',
                    backgroundColor: 'rgba(155,173,219, 0.5)',
                },
            ],
        },
        {
            responsive: true,
            scales: {
                y: {
                    beginAtZero: true,
                    title: {
                        display: true,
                        text: unitCurrencyText,
                        font: {
                            size: '10px',
                        }
                    },
                    ticks: {
                        callback: function (value) {
                            return value.toFixed(2);
                        },
                    },
                }
            },
            plugins: {
                legend: {
                    position: 'top',
                    labels: {
                        boxWidth: 10,
                        boxHeight: 10,
                    },
                },
                // title: {
                //     display: true,
                //     text: 'Free Cash Flow',
                // },
            },
        }]);

        setOperatingCashFlowChart([{
            labels: operatingCashFlowLabels,
            datasets: [
                {
                    label: 'Operating Cash Flow Ratio',
                    data: cashFlowOperationsData?.map((value) => value.toFixed(2)),
                    backgroundColor: 'rgb(240,139,73)',
                },
                {
                    label: 'Cashflow Coverage Ratio',
                    data: cashflowCoverageRatio?.map((value) => value.toFixed(2)),
                    backgroundColor: 'rgb(72,119,199)',
                }
            ],
        },
        {
            responsive: true,
            scales: {
                y: {
                    // title: {
                    //     display: true,
                    //     text: "times",
                    //     font: {
                    //         size: '10px',
                    //     }
                    // },
                    ticks: {
                        callback: function (value) {
                            return value.toFixed(2);
                        },
                    },
                },
            },
            plugins: {
                legend: {
                    position: 'top',
                    labels: {
                        boxWidth: 10,
                        boxHeight: 10,
                    },
                },
                // title: {
                //     display: true,
                //     text: 'Cash Flow',
                // },
            },
        }]);

        setCashConversionCycleChart([{
            labels: cashConversionCycleRatioLabels,
            datasets: [
                {
                    label: 'CCC',
                    data: cashConversionCycleData,
                    borderColor: 'rgb(155,173,219)',
                    backgroundColor: 'rgba(155,173,219, 0.5)',
                },
            ],
        },
        {
            responsive: true,
            scales: {
                y: {
                    beginAtZero: true,
                    title: {
                        display: true,
                        text: `days`,
                        font: {
                            size: '10px',
                        }
                    }
                }
            },
            plugins: {
                legend: {
                    position: 'top',
                    labels: {
                        boxWidth: 10,
                        boxHeight: 10,
                    },
                },
                // title: {
                //     display: true,
                //     text: 'Cash Conversion Cycle',
                // },
            },
        }]);

        setEfficiencyRatioChart([{
            labels: efficiencyRatioLabels,
            datasets: [
                {
                    label: 'DSO',
                    data: daySalesOutstanding,
                    backgroundColor: 'rgb(68,114,196)',
                    barPercentage: 1, // Adjust the width of the bars for the dataset
                },
                {
                    label: 'DPO',
                    data: dayPayablesOutstanding,
                    backgroundColor: 'rgb(237,125,49)',
                    barPercentage: 1, // Adjust the width of the bars for the dataset
                },
                {
                    label: 'DIO',
                    data: daysInventoryOutstanding,
                    backgroundColor: 'rgb(22,47,70)',
                    barPercentage: 1, // Adjust the width of the bars for the dataset
                },
            ],
        },
        {
            responsive: true,
            scales: {
                y: {
                    title: {
                        display: true,
                        text: `days`,
                        font: {
                            size: '10px',
                        }
                    }
                }
            },
            plugins: {
                legend: {
                    position: 'top',
                    labels: {
                        boxWidth: 10,
                        boxHeight: 10,
                    },
                },
                // title: {
                //     display: true,
                //     text: 'Efficiency Ratios',
                // },
            },
        }])
        setKeyFinancialRatioChart(keyFinancials)
        setIsLoading(false)
        setGraphData(resData)
    }

    useEffect(() => {
        (resData?.max_year === 0 || !resData?.max_year) && localStorage.removeItem('selectedObject')
        getGraphData();
    }, [resData])

    return (
        <div className='relative rounded-[5px] p-4 bg-white mb-3'>
            {showGraph && <div className=''>
                <div className="mb-4">
                    <p className="text-[16px] font-bold text-left mb-5">Profitability</p>
                    <div className='flex w-full flex-wrap'>
                        <div className="p-3 mb-4 sm:w-full md:w-1/2">
                            <p className="text-[14px] text-left font-500">Revenue to Profit</p>
                            <div className="relative rounded-[5px]">
                                <div className={revenueProfitChart?.length > 0 && revenueProfitChart[0]?.labels?.length === 0 && "blur-sm"}>
                                    {isLoading ? <div className="w-full h-[100px] flex justify-center items-center"><Spinner /></div> : revenueProfitChart?.length > 0 && <AreaChart prop={revenueProfitChart} />}
                                </div>
                                {revenueProfitChart?.length > 0 && revenueProfitChart[0]?.labels?.length === 0 && <HideData message={'Chart not available'} dataMessage={!edit ? "" : graphData?.profitability?.revenue_to_earnings_graph?.message ?? ""} />}
                            </div>
                        </div>
                        <div className="p-3 mb-4 sm:w-full md:w-1/2">
                            <p className="text-[14px] text-left font-500">Return on Assets and Equity</p>
                            <div className="relative rounded-[5px]">
                                <div className={roeRoaChart?.length > 0 && roeRoaChart[0]?.labels?.length === 0 && "blur-sm"}>
                                    {isLoading ? <div className="w-full h-[100px] flex justify-center items-center"><Spinner /></div> : roeRoaChart?.length > 0 && <HorizontalBarChart prop={roeRoaChart} />}
                                </div>
                                {roeRoaChart?.length > 0 && roeRoaChart[0]?.labels?.length === 0 && <HideData message={'Chart not available'} dataMessage={!edit ? "" : graphData?.profitability?.roe_to_roa?.message ?? ""} />}
                            </div>
                        </div>
                    </div>
                </div>
                <div className="mb-4">
                    <p className="text-[16px] font-bold text-left mb-5">Liquidity</p>
                    <div className='flex w-full flex-wrap'>
                        <div className="p-3 mb-4 sm:w-full md:w-1/2">
                            <p className="text-[14px] text-left font-500">Liquidity</p>
                            <div className="relative rounded-[5px]">
                                <div className={quickAssetChart?.length > 0 && quickAssetChart[0]?.labels?.length === 0 && "blur-sm"}>
                                    {isLoading ? <div className="w-full h-[100px] flex justify-center items-center"><Spinner /></div> : quickAssetChart?.length > 0 && <VerticalBarChart prop={quickAssetChart} />}
                                </div>
                                {quickAssetChart?.length > 0 && quickAssetChart[0]?.labels?.length === 0 && <HideData message={'Chart not available'} dataMessage={!edit ? "" : graphData?.liquidity?.quick_assets_to_liabilities?.message ?? ""} />}
                            </div>
                        </div>
                        <div className="p-3 mb-4 sm:w-full md:w-1/2">
                            <p className="text-[14px] text-left font-500">Cash Flow</p>
                            <div className="relative rounded-[5px]">
                                <div className={operatingCashFlowChart?.length > 0 && operatingCashFlowChart[0]?.labels?.length === 0 && "blur-sm"}>
                                    {isLoading ? <div className="w-full h-[100px] flex justify-center items-center"><Spinner /></div> : operatingCashFlowChart?.length > 0 && <HorizontalBarChart prop={operatingCashFlowChart} />}
                                </div>
                                {operatingCashFlowChart?.length > 0 && operatingCashFlowChart[0]?.labels?.length === 0 && <HideData message={'Chart not available'} dataMessage={!edit ? "" : graphData?.liquidity?.operating_cash_flow_ratio?.message ?? ""} />}
                            </div>
                        </div>
                        <div className="p-3 mb-4 sm:w-full md:w-1/2">
                            <p className="text-[14px] text-left font-500">Free Cash Flow</p>
                            <div className="relative rounded-[5px]">
                                <div className={freeCashChart?.length > 0 && freeCashChart[0]?.labels?.length === 0 && "blur-sm"}>
                                    {isLoading ? <div className="w-full h-[100px] flex justify-center items-center"><Spinner /></div> : freeCashChart?.length > 0 && <LineChart prop={freeCashChart} />}
                                </div>
                                {freeCashChart?.length > 0 && freeCashChart[0]?.labels?.length === 0 && <HideData message={'Chart not available'} dataMessage={!edit ? "" : graphData?.liquidity?.free_cash_flow_ratio?.message ?? ""} />}
                            </div>
                        </div>
                    </div>
                </div>
                <div className="mb-4">
                    <p className="text-[16px] font-bold text-left mb-5">Efficiency</p>
                    <div className='flex w-full flex-wrap'>
                        <div className="p-3 mb-4 sm:w-full md:w-1/2">
                            <p className="text-[14px] text-left font-500">Efficiency Ratios</p>
                            <div className="relative rounded-[5px]">
                                <div className={efficiencyRatioChart?.length > 0 && efficiencyRatioChart[0]?.labels?.length === 0 && "blur-sm"}>
                                    {isLoading ? <div className="w-full h-[100px] flex justify-center items-center"><Spinner /></div> : efficiencyRatioChart?.length > 0 && <VerticalBarChart prop={efficiencyRatioChart} />}
                                </div>
                                {efficiencyRatioChart?.length > 0 && efficiencyRatioChart[0]?.labels?.length === 0 && <HideData message={'Chart not available'} dataMessage={!edit ? "" : graphData?.efficiency?.efficiency_ratios?.message ?? ""} />}
                            </div>
                        </div>
                        <div className="p-3 mb-4 sm:w-full md:w-1/2">
                            <p className="text-[14px] text-left font-500">Cash Conversion Cycles</p>
                            <div className="relative rounded-[5px]">
                                <div className={cashConversionCycleChart?.length > 0 && cashConversionCycleChart[0]?.labels?.length === 0 && "blur-sm"}>
                                    {isLoading ? <div className="w-full h-[100px] flex justify-center items-center"><Spinner /></div> : cashConversionCycleChart?.length > 0 && <LineChart prop={cashConversionCycleChart} />}
                                </div>
                                {cashConversionCycleChart?.length > 0 && cashConversionCycleChart[0]?.labels?.length === 0 && <HideData message={'Chart not available'} dataMessage={!edit ? "" : graphData?.efficiency?.cash_conversion_cycle?.message ?? ""} />}
                            </div>
                        </div>
                        <div className="p-3 text-left mb-4 w-full">
                            <p>Key Ratios</p>
                            <div className='relati '>
                                {(keyFinancialRatioChart?.quick_ratio?.data?.value || keyFinancialRatioChart?.operating_cashflow_ratio?.data?.value || keyFinancialRatioChart?.gearing_ratio?.data?.value) ? <div className="relative w-full flex flex-row flex-wrap items-start md:justify-between lg:justify-evenly p-2">
                                    {keyFinancialRatioChart?.quick_ratio?.data?.value && <div className="flex items-center justify-center w-[260px]">
                                        <div className="w-[240px]">
                                            <p className="text-[20px] font-500 w-full text-center">
                                                Quick Ratio
                                            </p>
                                            <p className="text-[40px] font-600 w-full text-center">{keyFinancialRatioChart?.quick_ratio?.data?.value?.toFixed(2)}</p>
                                            <p className="text-[10px] text-center">{keyFinancialRatioChart?.quick_ratio?.data?.year}</p>
                                        </div>
                                        {keyFinancialRatioChart?.quick_ratio?.data?.positive ? <UpArrow width='40px' height='40px' className='mt-[28px]' /> : <DownArrow width='40px' height='40px' className='mt-[28px]' />}
                                    </div>}
                                    {keyFinancialRatioChart?.gearing_ratio?.data?.value && <div className="flex items-center w-[260px]">
                                        <div className="w-[240px]">
                                            <p className="text-[20px] font-500 w-full text-center">
                                                Gearing Ratio
                                            </p>
                                            <p className="text-[40px] font-600 w-full text-center">{keyFinancialRatioChart?.gearing_ratio?.data?.value?.toFixed(2)}</p>
                                            <p className="text-[10px] text-center">{keyFinancialRatioChart?.gearing_ratio?.data?.year}</p>
                                        </div>
                                        {keyFinancialRatioChart?.gearing_ratio?.data?.positive ? <UpArrow width='40px' height='40px' className='mt-[28px]' /> : <DownArrow width='40px' height='40px' className='mt-[28px]' />}
                                    </div>}
                                    {keyFinancialRatioChart?.operating_cashflow_ratio?.data?.value && <div className="flex items-center w-[260px]">
                                        <div className="w-[240px]">
                                            <p className="text-[20px] font-500 w-full text-center">
                                                Operating Cashflow
                                            </p>
                                            <p className="text-[40px] font-600 w-full text-center">{keyFinancialRatioChart?.operating_cashflow_ratio?.data?.value?.toFixed(2)}</p>
                                            <p className="text-[10px] text-center">{keyFinancialRatioChart?.operating_cashflow_ratio?.data?.year}</p>
                                        </div>
                                        {keyFinancialRatioChart?.operating_cashflow_ratio?.data?.positive ? <UpArrow width='40px' height='40px' className='mt-[28px]' /> : <DownArrow width='40px' height='40px' className='mt-[28px]' />}
                                    </div>}
                                </div> : <div className='h-[200px]'><HideData message={'Chart not available'} dataMessage={!edit ? "" : "Quick Ratio, Gearing Ratio & Operating Ratio value missing"} /></div>}
                            </div>
                        </div>
                    </div>
                </div>
            </div>}
            {!edit && !showGraph && <div className='h-[200px]'><HideData message={'Charts not available'} dataMessage={!edit ? "" : "Please provide the required financial details to generate the financial graph."} /></div>}
        </div>
    )
}

export default FinancialCharts